import type {
  AnalyticsFeeder,
  GrafanaReport,
  GrafanaReportFilters,
  ReportGrouping,
} from "@/lib/api";
import { DEFAULT_DASHBOARD_RANGE, normalizeDashboardRange } from "@/lib/dashboard-time";

export const ALL_REPORT_VALUES = "__ALL__";

export type ActiveGrafanaReport = Exclude<GrafanaReport, "help">;

export const REPORTS: ReadonlyArray<{ report: ActiveGrafanaReport; title: string; path: string }> =
  [
    { report: "executive", title: "Executive Summary", path: "/dashboard" },
    { report: "performance", title: "Performance", path: "/reports/performance" },
    { report: "reliability", title: "Reliability", path: "/reports/reliability" },
    { report: "daily-dar", title: "Daily DAR", path: "/reports/daily-dar" },
  ];

export type CatalogueFilter =
  | "program"
  | "vendor"
  | "disco"
  | "controller"
  | "state"
  | "substation"
  | "voltage_class"
  | "band"
  | "station_type"
  | "regulator"
  | "nerc_class"
  | "phase"
  | "status"
  | "feeder";

export const CATALOGUE_FILTERS: ReadonlyArray<{
  key: CatalogueFilter;
  label: string;
  field: keyof AnalyticsFeeder;
  searchKey: string;
}> = [
  { key: "program", label: "Program", field: "program_id", searchKey: "program" },
  { key: "vendor", label: "IoT Vendor", field: "vendor", searchKey: "vendor" },
  { key: "disco", label: "DisCo", field: "disco", searchKey: "disco" },
  { key: "controller", label: "Controller", field: "device_id", searchKey: "controller" },
  { key: "state", label: "State", field: "state", searchKey: "state" },
  { key: "substation", label: "Substation", field: "substation", searchKey: "substation" },
  { key: "voltage_class", label: "Voltage", field: "voltage_class", searchKey: "class" },
  { key: "band", label: "Band", field: "band", searchKey: "band" },
  { key: "station_type", label: "Station Type", field: "station_type", searchKey: "station_type" },
  { key: "regulator", label: "Regulator", field: "regulators", searchKey: "regulator" },
  {
    key: "nerc_class",
    label: "NERC Class",
    field: "nerc_service_classes",
    searchKey: "nerc_class",
  },
  { key: "phase", label: "IoT Phase", field: "iot_phase", searchKey: "phase" },
  { key: "status", label: "IoT Status", field: "iot_installed", searchKey: "status" },
  { key: "feeder", label: "Feeder", field: "dev_eui", searchKey: "feeder" },
];

export function catalogueValues(
  feeder: AnalyticsFeeder,
  definition: (typeof CATALOGUE_FILTERS)[number],
): string[] {
  if (definition.key === "status")
    return [
      feeder.iot_installed == null
        ? "Unknown"
        : feeder.iot_installed
          ? "Installed"
          : "Not Installed",
    ];
  if (definition.key === "state") return feeder.served_states ?? [feeder.state];
  const value = feeder[definition.field];
  return Array.isArray(value) ? value : value == null ? [] : [String(value)];
}

export const REPORT_RANGES: ReadonlyArray<{ label: string; from: string; to: string }> = [
  { label: "Last 6 hours", from: "now-6h", to: "now" },
  { label: "Last 24 hours", from: "now-24h", to: "now" },
  { label: "Yesterday", from: "now-1d/d", to: "now-1d/d" },
  { label: "Last 3 days", ...DEFAULT_DASHBOARD_RANGE },
  { label: "Last 7 days", from: "now-7d", to: "now" },
  { label: "Last 30 days", from: "now-30d", to: "now" },
];

export function readReportFilters(search: URLSearchParams): GrafanaReportFilters {
  const grouping: string | null = search.get("groupby");
  return {
    program: search.get("program") ?? "",
    vendor: search.get("vendor") ?? ALL_REPORT_VALUES,
    disco: search.get("disco") ?? ALL_REPORT_VALUES,
    controller: search.get("controller") ?? ALL_REPORT_VALUES,
    state: search.get("state") ?? ALL_REPORT_VALUES,
    substation: search.get("substation") ?? ALL_REPORT_VALUES,
    voltage_class: search.get("class") ?? search.get("voltage_class") ?? ALL_REPORT_VALUES,
    band: search.get("band") ?? ALL_REPORT_VALUES,
    feeder: search.get("feeder") ?? ALL_REPORT_VALUES,
    station_type: search.get("station_type") ?? ALL_REPORT_VALUES,
    regulator: search.get("regulator") ?? ALL_REPORT_VALUES,
    nerc_class: search.get("nerc_class") ?? ALL_REPORT_VALUES,
    phase: search.get("phase") ?? ALL_REPORT_VALUES,
    status: search.get("status") ?? ALL_REPORT_VALUES,
    groupby: (grouping === "DISCO" || grouping === "Substation"
      ? grouping
      : "Overall") as ReportGrouping,
    ...normalizeDashboardRange(
      search.get("from") ?? DEFAULT_DASHBOARD_RANGE.from,
      search.get("to") ?? DEFAULT_DASHBOARD_RANGE.to,
    ),
  };
}

export function normalizeReportFilters(
  filters: GrafanaReportFilters,
  feeders: readonly AnalyticsFeeder[],
  report: GrafanaReport,
): GrafanaReportFilters {
  const normalized: { filters: GrafanaReportFilters; feeders: readonly AnalyticsFeeder[] } =
    CATALOGUE_FILTERS.reduce(
      (scope, definition) => {
        const values: string[] = Array.from(
          new Set(scope.feeders.flatMap((feeder) => catalogueValues(feeder, definition))),
        );
        let selected: string = scope.filters[definition.key] ?? ALL_REPORT_VALUES;
        if (definition.key === "voltage_class" && ["11", "33"].includes(selected)) selected += "kV";
        if (definition.key === "substation" && !values.includes(selected)) {
          const aliases = new Set(
            scope.feeders
              .filter((feeder) => feeder.legacy_substation === selected)
              .map((feeder) => feeder.substation),
          );
          if (aliases.size === 1) selected = [...aliases][0] ?? selected;
        }
        const mustSelect: boolean =
          definition.key === "program" || (definition.key === "feeder" && report === "performance");
        const value: string = values.includes(selected)
          ? selected
          : mustSelect && (selected === "" || selected === ALL_REPORT_VALUES)
            ? (values[0] ?? "")
            : selected;
        return {
          filters: { ...scope.filters, [definition.key]: value },
          feeders:
            value === ALL_REPORT_VALUES
              ? scope.feeders
              : scope.feeders.filter((feeder) =>
                  catalogueValues(feeder, definition).includes(value),
                ),
        };
      },
      { filters: { ...filters, ...normalizeDashboardRange(filters.from, filters.to) }, feeders },
    );
  return normalized.filters;
}

export function catalogueOptions(
  feeders: readonly AnalyticsFeeder[],
  filters: GrafanaReportFilters,
  key: CatalogueFilter,
): AnalyticsFeeder[] {
  const upstream = CATALOGUE_FILTERS.slice(
    0,
    CATALOGUE_FILTERS.findIndex((definition) => definition.key === key),
  );
  return feeders.filter((feeder) =>
    upstream.every(
      (definition) =>
        (filters[definition.key] ?? ALL_REPORT_VALUES) === ALL_REPORT_VALUES ||
        catalogueValues(feeder, definition).includes(filters[definition.key] ?? ALL_REPORT_VALUES),
    ),
  );
}

export function visibleReportFeeders(
  feeders: readonly AnalyticsFeeder[],
  filters: GrafanaReportFilters,
): AnalyticsFeeder[] {
  return feeders.filter((feeder) =>
    CATALOGUE_FILTERS.every(
      (definition) =>
        (filters[definition.key] ?? ALL_REPORT_VALUES) === ALL_REPORT_VALUES ||
        catalogueValues(feeder, definition).includes(filters[definition.key] ?? ALL_REPORT_VALUES),
    ),
  );
}

export function reportSearchParams(
  filters: GrafanaReportFilters,
  search: URLSearchParams = new URLSearchParams(),
): URLSearchParams {
  const params = new URLSearchParams(search);
  CATALOGUE_FILTERS.forEach((definition) =>
    params.set(definition.searchKey, filters[definition.key] ?? ALL_REPORT_VALUES),
  );
  params.delete("voltage_class");
  params.set("groupby", filters.groupby);
  params.set("from", filters.from);
  params.set("to", filters.to);
  return params;
}
